import { useEffect } from 'react';

const BASE_URL = 'https://hoibuuralmere.nl';
const DEFAULT_IMAGE = 'https://hoibuuralmere.nl/images/og-image.png';

// Indexering-beleid:
// ─────────────────
// Wij willen dat de OFFICIËLE productie-URL (hoibuuralmere.nl en subdomeinen)
// door zoekmachines wordt geïndexeerd. Alleen bekende test/preview omgevingen
// mogen NIET indexeren.
//
// Bug fix (aug 2026): eerder gebruikten we een whitelist van productie-hosts,
// maar de pre-render service van Emergent draait de JS binnen een interne
// container (niet-hoibuuralmere-hostname) terwijl de resulterende HTML wél
// aan Googlebot voor hoibuuralmere.nl wordt geserveerd. Die pre-render viel
// dus buiten de whitelist en kreeg per ongeluk `noindex`, waardoor Google
// de homepage niet indexeerde (Julian's GSC melding).
//
// Nieuwe aanpak: expliciete BLOCKLIST van test-hostnames. Alles wat NIET
// op de blocklist staat (inclusief pre-render contexten) krijgt `index,
// follow`. De canonical URL (hieronder) wijst altijd naar de productie-URL
// zodat Google indien nodig kan consolideren.
const NON_INDEXABLE_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  'testhoibuur.nl',
  'www.testhoibuur.nl',
]);

const NON_INDEXABLE_SUFFIXES = [
  '.preview.emergentagent.com',   // preview builds
];

const isNonIndexableHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (NON_INDEXABLE_HOSTS.has(host)) return true;
  return NON_INDEXABLE_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

const setMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setJsonLd = (data) => {
  const id = 'seo-jsonld';
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const SEO = ({ title, description, path = '', image, type = 'website', jsonLd, titleOverride = false }) => {
  const fullTitle = title ? (titleOverride ? title : `${title} | Hoi Buur Almere`) : 'Hoi Buur Almere - Ontdek wat er te doen is in jouw buurt';
  const fullDescription = description || 'Er gebeurt zoveel moois in Almere. Ontdek activiteiten bij jou in de buurt, van sport tot cultuur. Gratis en voor iedereen!';
  const url = `${BASE_URL}${path}`;
  const rawImage = image || DEFAULT_IMAGE;
  const ogImage = rawImage.startsWith('http')
    ? rawImage
    : `${BASE_URL}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;

  useEffect(() => {
    document.title = fullTitle;

    // Robots: alleen indexen op productie (hoibuuralmere.nl). Op preview/test
    // domeinen alles blokkeren om duplicate-content + verwarring in Google
    // te voorkomen.
    const robotsContent = !isNonIndexableHost()
      ? 'index, follow'
      : 'noindex, nofollow, noarchive';
    setMeta('name', 'robots', robotsContent);
    setMeta('name', 'googlebot', robotsContent);

    setMeta('name', 'description', fullDescription);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', fullDescription);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:locale', 'nl_NL');
    setMeta('property', 'og:site_name', 'Hoi Buur Almere');
    setMeta('property', 'twitter:card', 'summary_large_image');
    setMeta('property', 'twitter:title', fullTitle);
    setMeta('property', 'twitter:description', fullDescription);
    setMeta('property', 'twitter:image', ogImage);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // JSON-LD structured data
    if (jsonLd) {
      setJsonLd(jsonLd);
    } else {
      // Default Organization schema
      setJsonLd({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Hoi Buur Almere',
        url: BASE_URL,
        description: fullDescription,
        publisher: {
          '@type': 'Organization',
          name: 'Positief Gezond Almere',
          url: 'https://positiefgezondalmere.nl'
        }
      });
    }
  }, [fullTitle, fullDescription, url, ogImage, type, jsonLd]);

  return null;
};
